import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ArrowUpRight } from "lucide-react";
import { WHATSAPP, waLink } from "../data/site";

const WhatsAppIcon = ({ size = 26 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M20.52 3.48A11.87 11.87 0 0 0 12.04 0C5.46 0 .1 5.35.1 11.93c0 2.1.55 4.16 1.6 5.97L0 24l6.27-1.64a11.9 11.9 0 0 0 5.76 1.47h.01c6.58 0 11.93-5.35 11.93-11.93 0-3.19-1.24-6.19-3.45-8.42ZM12.04 21.8a9.9 9.9 0 0 1-5.05-1.38l-.36-.21-3.72.97 1-3.63-.24-.37a9.86 9.86 0 0 1-1.51-5.25c0-5.45 4.44-9.88 9.9-9.88 2.64 0 5.12 1.03 6.98 2.9a9.8 9.8 0 0 1 2.89 6.98c0 5.45-4.44 9.88-9.89 9.88Zm5.42-7.4c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.96-.94 1.16-.17.2-.35.22-.64.07-.3-.15-1.26-.46-2.4-1.47-.88-.79-1.48-1.76-1.65-2.06-.17-.3-.02-.46.13-.6.13-.14.3-.35.44-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.5 0 1.47 1.07 2.9 1.22 3.1.15.2 2.1 3.2 5.1 4.49.71.3 1.27.49 1.7.63.72.23 1.37.2 1.88.12.58-.09 1.76-.72 2-1.42.25-.7.25-1.29.18-1.42-.07-.13-.27-.2-.57-.35Z" />
  </svg>
);

export const WhatsAppFloat = () => {
  const [open, setOpen] = useState(false);

  return (
    <div className="fixed bottom-6 right-6 z-[60]">
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.96 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="absolute bottom-20 right-0 w-80 overflow-hidden rounded-3xl border border-white/10 bg-silt/95 shadow-[0_24px_60px_rgba(0,0,0,0.55)] backdrop-blur-xl"
            data-testid="whatsapp-popup"
          >
            <div className="flex items-center justify-between border-b border-white/10 bg-wa/10 px-5 py-4">
              <div>
                <p className="font-display text-sm font-semibold text-cream">WhatsApp Concierge</p>
                <p className="mt-1 flex items-center gap-2 text-xs text-fog">
                  <span className="pulse-dot inline-block h-2 w-2 rounded-full bg-wa" />
                  Online · Avg response 5 mins
                </p>
              </div>
              <button
                onClick={() => setOpen(false)}
                aria-label="Close WhatsApp popup"
                className="text-fog transition-colors hover:text-cream"
                data-testid="whatsapp-popup-close-button"
              >
                <X size={18} />
              </button>
            </div>
            <div className="space-y-2 p-4">
              {WHATSAPP.presets.map((msg, i) => (
                <a
                  key={i}
                  href={waLink(msg)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`whatsapp-popup-option-${i}`}
                  className="group flex items-start justify-between gap-3 rounded-2xl border border-white/5 bg-steel/60 p-4 text-xs leading-relaxed text-fog transition-all duration-300 hover:border-wa/40 hover:text-cream"
                >
                  <span>{msg}</span>
                  <ArrowUpRight size={14} className="mt-0.5 shrink-0 text-wa transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
              ))}
              <p className="px-1 pt-1 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-ash">
                {WHATSAPP.display}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        onClick={() => setOpen((v) => !v)}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.94 }}
        aria-label="Chat on WhatsApp"
        data-testid="floating-whatsapp-button"
        className="flex h-14 w-14 items-center justify-center rounded-full bg-wa text-obsidian shadow-[0_12px_36px_rgba(37,211,102,0.35)] transition-shadow duration-300 hover:shadow-[0_12px_44px_rgba(37,211,102,0.55)]"
      >
        {open ? <X size={24} /> : <WhatsAppIcon />}
      </motion.button>
    </div>
  );
};
